import React, {useEffect, useRef, useState} from "react";
import {get, patch, post} from "../../api/api";
import {formatDateTime} from "../../utils/date";
import {EncounterModel} from "../../models/EncounterModel";
import {TranscriptBox} from "./TranscriptBox";
import {ReportBox} from "./ReportBox";
import {useAppStore} from "../../store/appStore";
import {
    Button,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Slide,
    Stack,
    Tab,
    Tabs,
    TextField,
    useMediaQuery
} from "@mui/material";
import {TemplateModel} from "../../models/TemplateModel";
import TemplateDetails from "../Template/TemplateDetail";
import {showMessage} from "../../utils/snackbar";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CachedIcon from "@mui/icons-material/Cached";
import {AudioChannel} from "../../services/audio/AudioChannel";
import StopIcon from "@mui/icons-material/Stop";
import SendIcon from "@mui/icons-material/Send";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {drawerWidth} from "../../pages/App";
import theme from "../../theme";
import {useWakeLock} from 'react-screen-wake-lock';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {AIDialog} from "./AIDialog";
import {MicPermissionDialog} from "./MicPermissionDialog";
import {useAuthStore} from "../../store/authStore";
import {logEvent} from "firebase/analytics";
import {firebaseAnalytics} from "../../firebaseConfig";
import {useOnlineStatusWithTimeout} from "../../hooks/useOnlineStatus";


export type TabValueType = "in-person" | "screen-sharing"
type ViewValueType = "template" | "transcript" | "report"


export interface EncounterProps {
    id?: string | null,
    sidebarOpen: boolean,
}

const STICKY_PANEL_SHOW_Y = 200;
const FLUSH_EVERY_MSEC = 60 * 1000;
const BETA_TESTER_WHITE_LIST = new Set([
    "andrei@medendo.com",
    "arjan@medendo.com",
]);

const Encounter: React.FC<EncounterProps> = ({id, sidebarOpen}) => {
    const payload = useAuthStore(state => state.payload);
    const isBetaTester = BETA_TESTER_WHITE_LIST.has(payload?.email?.toLowerCase());

    const templates = useAppStore((state) => state.templates);
    const isIntegration = useAppStore((state) => state.isIntegration);
    const selectedTemplateId = useAppStore((state) => state.selectedTemplateId);

    const [name, setName] = useState<string>(formatDateTime(new Date()));
    const [template, setTemplate] = useState<TemplateModel | null>(null);
    const [encounter, setEncounter] = useState<EncounterModel | null>(null);

    const [isRecording, setIsRecording] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [meetingType, setMeetingType] = React.useState<TabValueType>('in-person');
    const [viewType, setViewType] = React.useState<ViewValueType>('template');
    const [language, setLanguage] = useState('nl');
    const [showStickyPanel, setShowStickyPanel] = useState(false);
    const [modifyAIOpen, setModifyAIOpen] = useState(false);
    const [micDialogOpen, setMicDialogOpen] = useState(false);

    const [aiText, setAIText] = useState("");
    const [uncompletedTranscript, setUncompletedTranscript] = useState("");

    const doctorAC = useRef<AudioChannel | null>(null);
    const clientAC = useRef<AudioChannel | null>(null);

    const bottomRef = useRef<HTMLDivElement | null>(null);

    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [isRecordingHovered, setRecordingHovered] = useState(false);
    const isReleasedRef = useRef(true);
    const {isSupported, request, release} = useWakeLock({
        onRequest: () => {
            isReleasedRef.current = false
            console.info("[Encounter] Wake lock requested");
        },
        onRelease: () => {
            isReleasedRef.current = true
            console.info("[Encounter] Wake lock released");
        },
        onError: (error) => {
            console.error("[Encounter] Wake lock error:", error);
        },
    });

    const transcriptBuffer = useRef<string[]>([]);
    const transcriptTimer = useRef<NodeJS.Timeout | null>(null);
    const [aiProvider, setAiProvider] = useState('')
    const [wsProvider, setWSProvider] = useState('')
    const offlineTimeoutExceeded = useOnlineStatusWithTimeout();


    useEffect(() => {
        console.info("[Encounter] Selected template id:", selectedTemplateId);
        if (templates.length === 0) return;
        const template = templates.find((t) => t.id === selectedTemplateId);
        if (template) {
            setTemplate(template);
            return;
        }
        console.info("[Encounter] No template selected, using first template");
        setTemplate(templates[0]);
    }, [selectedTemplateId, templates]);

    useEffect(() => {
        const fetchEncounter = async (encounterId: string) => {
            try {
                const data = await get(`/encounters/${encounterId}`);
                console.info("Encounter loaded", data);
                setEncounter(data);
                setName(data.name);
                setTemplate(data.template);
            } catch (error) {
                console.warn("Failed to load encounter:", error);
                showMessage("Er is een fout opgetreden bij het laden van de afspraak.", "error")
            }
        };

        if (id) {
            fetchEncounter(id);
        }
    }, [id]);

    const encounterRef = useRef(encounter);
    useEffect(() => {
        encounterRef.current = encounter;
    }, [encounter]);

    useEffect(() => {
        if (isIntegration) return;

        const handleScroll = () => {
            const scrolled = window.scrollY > STICKY_PANEL_SHOW_Y;
            setShowStickyPanel(scrolled);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        }
    }, [isIntegration]);

    // unmount component
    useEffect(() => {
        return () => {
            console.info("[Encounter] Component unmounted, clearing resources");
            clearTranscriptInterval();
            transcriptBuffer.current = [];
            doctorAC.current?.stop();
            clientAC.current?.stop();
        };
    }, []);

    const onAddScore = async (score: number) => {
        await updateEncounter({score: score});
        showMessage('Beoordeling toegevoegd, bedankt', 'info', 1000)
    };

    const handleReportChange = (newReport: Record<string, string>) => {
        setEncounter(prev => prev ? {...prev, report: newReport} : prev);
    };


    const modifyReportAI = async (text: string) => {
        if (!encounterRef.current || !text) return;

        setIsGeneratingReport(true);

        try {
            const temperature = 0.1
            const response = await post(`/encounters/${encounterRef.current.id}/report/modifications`, {
                'comment': text,
                'temperature': temperature
            })
            setAiProvider(response.ai_provider)
            logEvent(firebaseAnalytics, "modify_report", {
                temperature: temperature,
                ai_provider: response.ai_provider,
            })
            setEncounter((prev) => (prev ? {...prev, report: response.report} : prev));
        } catch (e: any) {
            console.warn(e)
            switch (e?.status) {
                case 404:
                    showMessage("De encounter kon niet worden gevonden.", "error", null)
                    break;
                case 400:
                    showMessage("Het wijzigen van het verslag is mislukt vanwege het inhoudsbeleid van Azure OpenAI. " +
                        "Probeer het opnieuw te wijzigen. " +
                        "Als het opnieuw mislukt, neem dan contact op met de ondersteuning.", "error", null)
                    break;
                default:
                    console.error(e)
                    showMessage("Er is een fout opgetreden bij het genereren van het verslag tijdens de modificatie.",
                        "error")
            }
        } finally {
            setIsGeneratingReport(false);
        }
    }

    const handleModifyReportAI = (text: string) => {
        modifyReportAI(text)
    }

    const handleModifyItemAI = async (key: string, text: string) => {
        if (!encounterRef.current || !text || !key) return;
        setIsGeneratingReport(true);

        try {
            const temperature = 0.1
            const response = await post(`/encounters/${encounterRef.current.id}/report/item_modifications`, {
                'name': key,
                'comment': text,
                'temperature': temperature
            })
            setAiProvider(response.ai_provider)
            logEvent(firebaseAnalytics, "modify_report_item", {
                temperature: temperature,
                ai_provider: response.ai_provider,
            })
            setEncounter((prev) => (prev ? {...prev, report: response.report} : prev));
        } catch (e: any) {
            console.warn(e)
            switch (e?.status) {
                case 404:
                    showMessage("Het door u geselecteerde element ontbreekt in het template en kan daarom niet worden " +
                        "gewijzigd (mogelijk is het template aangepast).", "error", null)
                    break;
                case 400:
                    showMessage("Het wijzigen van het verslag is mislukt vanwege het inhoudsbeleid van Azure OpenAI. " +
                        "Probeer het opnieuw te wijzigen. " +
                        "Als het opnieuw mislukt, neem dan contact op met de ondersteuning.", "error", null)
                    break;
                default:
                    console.error(e)
                    showMessage("Er is een fout opgetreden bij het genereren van het verslag tijdens de modificatie.", "error")
            }
        } finally {
            setIsGeneratingReport(false);
        }
    }

    const updateEncounter = async (updatedInfo: Partial<EncounterModel>) => {
        if (!encounterRef.current) return;

        const payload = Object.fromEntries(
            Object.entries(updatedInfo).filter(([_, value]) => value !== null && value !== undefined)
        );

        try {
            const response = await patch(`/encounters/${encounterRef.current.id}`, payload);
            setEncounter(response);
            setName(response.name);
            setTemplate(response.template);
        } catch (error) {
            console.warn("Failed to update encounter:", error);
            showMessage("Er is een fout opgetreden bij het bijwerken van de afspraak.", "error")
        }
    };

    const onStartStreaming = async () => {
        setViewType('transcript')
        try {
            if (!encounter) {
                const response = await post("/encounters", {name, template});
                console.info("Encounter created", response);
                setEncounter(response);
            }
        } catch (error) {
            console.warn("Failed to create encounter:", error);
            showMessage("Er is een fout opgetreden bij het starten van de afspraak.", "error")
        }
    };

    const updateTranscript = async (encounterId: string, append: string) => {
        if (!append) return;
        try {
            await patch(`/encounters/${encounterId}/transcript`, {append: append});
        } catch (error) {
            console.warn("Failed to update transcript:", error);
        }
    }

    const flushTranscriptBuffer = async () => {
        console.info('[Encounter] [Timer] flushTranscriptBuffer: ', transcriptBuffer.current)
        clearTranscriptInterval()
        try {
            // Reactivate wake lock
            if (isSupported && isReleasedRef.current && document.visibilityState === "visible") await request();
        } catch (error) {
            console.error("Failed to request wake lock:", error);
        }

        if (!encounterRef.current || transcriptBuffer.current.length === 0) return;

        const accumulatedTranscript = transcriptBuffer.current.join('\n');
        transcriptBuffer.current = [];

        await updateTranscript(encounterRef.current.id, accumulatedTranscript);
    };

    const onNewTranscript = async (append: string, completed: boolean) => {
        if (!append?.trim()) return;

        if (!completed) {
            setUncompletedTranscript(append);
            return;
        }

        setUncompletedTranscript("");

        transcriptBuffer.current.push(append);
        setEncounter((prev) => (prev ? {...prev, transcript: (prev.transcript ?? "") + '\n' + append} : prev));

        bottomRef?.current?.scrollIntoView({behavior: "smooth", block: "start"});

        if (!transcriptTimer.current) {
            console.info('[Encounter] [Timer] set flushTranscriptBuffer for 60 s')
            transcriptTimer.current = setInterval(flushTranscriptBuffer, FLUSH_EVERY_MSEC);
        }
    };

    const clearTranscriptInterval = () => {
        if (!transcriptTimer.current) return;

        console.info('[Encounter] [Timer] clear transcript interval')

        clearInterval(transcriptTimer.current);
        transcriptTimer.current = null;
    }

    const onStopStreaming = async () => {
        setIsRecording(false);
        setViewType('report')

        clearTranscriptInterval()
        transcriptBuffer.current = []

        if (encounterRef.current) {
            const fullTranscript = (encounterRef.current.transcript ?? "") + "\n" + uncompletedTranscript.trim();
            await updateEncounter({transcript: fullTranscript});
        }

        await generateReport(0.0, 'generate_report')
    };

    const onRegenerateReport = async () => {
        await generateReport(1, 'regenerate_report')
    }

    const generateReport = async (temperature: number, event_name: string) => {
        if (!encounterRef.current?.transcript) return;

        setIsGeneratingReport(true);

        try {
            const response = await post(`/encounters/${encounterRef.current.id}/report`, {'temperature': temperature});
            console.info("Report generated.", response.report);
            setAiProvider(response.ai_provider)
            logEvent(firebaseAnalytics, event_name, {
                temperature: temperature,
                ai_provider: response.ai_provider,
            })
            setEncounter((prev) => (prev ? {...prev, report: response.report} : prev));
        } catch (e: any) {
            console.warn(e)
            switch (e?.status) {
                case 404:
                    showMessage("De encounter kon niet worden gevonden.", "error", null)
                    break;
                case 400:
                    showMessage("Het wijzigen van het verslag is mislukt vanwege het inhoudsbeleid van Azure OpenAI. " +
                        "Probeer het opnieuw te wijzigen. " +
                        "Als het opnieuw mislukt, neem dan contact op met de ondersteuning.", "error", null)
                    break;
                default:
                    console.error(e)
                    showMessage("Er is een fout opgetreden bij het genereren van het verslag.", "error")
            }
        } finally {
            setIsGeneratingReport(false);
        }
    }

    const onTemplateChange = async (event: SelectChangeEvent) => {
        const newTemplateId = event.target.value;
        const newTemplate = templates.find((t) => t.id === newTemplateId);
        if (newTemplate) {
            setTemplate(newTemplate);
            if (encounter) {
                await updateEncounter({template: newTemplate});
            }
        }
    };

    const onNameChange = (e: { target: { value: any } }) => {
        setName(e.target.value);
    };

    const onNameBlur = async () => {
        if (encounter) {
            await updateEncounter({name: name});
            setEncounter((prev) => (prev ? {...prev, name} : prev));
        }
    };

    const onNameKeyDown = async (e: { key: string; preventDefault: () => void }) => {
        if (e.key === "Enter" && encounter) {
            e.preventDefault();
            await updateEncounter({name: name});
            setEncounter((prev) => (prev ? {...prev, name} : prev));
        }
    };

    const publishApiEventToParentFrame = (resource_name: string, payload: any) => {
        if (window.parent === undefined) {
            return;
        }

        // nosemgrep javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration - we restrict embedding through the Content-Security-Policy header
        window.parent.postMessage({resource_name, payload}, "*");
    };

    const handleCopy = async () => {
        if (!encounter?.report) return;

        const reportText = Object.entries(encounter?.report)
            .map(([key, value]) => `${key}\n${value || "onbekend"}`)
            .join("\n\n");

        try {
            await navigator.clipboard.writeText(reportText);
            showMessage('Gekopieerd', 'success', 1000)
        } catch (error) {
            console.error("Failed to copy:", error);
        }
        try {
            console.log("encounter", encounter);
            if (encounter?.report && isIntegration) {
                console.log("encounter.report", encounter.report);
                publishApiEventToParentFrame("report", {report: encounter.report});
            }
        } catch (error) {
            console.error("Failed to publish API event:", error);
        }
    };

    const handleRecordingClick = async () => {
        if (meetingType === 'screen-sharing') {
            console.info("Screen sharing");
            isRecording ? await handleStopScreenShare() : await handleStartScreenShare()
        } else {
            console.info("Recording");
            isRecording ? await handleStopRecording() : await handleStartRecording()
        }
    }

    const handleStartScreenShare = async () => {
        try {
            setIsRecording(true);

            // Doctor's microphone
            const errorHandler = () => {
                handleStopScreenShare();
                showMessage("Er is een fout opgetreden bij het starten van het scherm delen.", "error");
            }

            doctorAC.current = new AudioChannel(
                language,
                (t, completed) => onNewTranscript(`[behandelaar]:\t${t}`, completed),
                errorHandler
            );

            // Client's screen audio
            clientAC.current = new AudioChannel(
                language,
                (t, completed) => onNewTranscript(`[client]:\t${t}`, completed),
                errorHandler
            );

            let ws_provider = await doctorAC.current.connect();
            ws_provider += ', ';
            ws_provider += await clientAC.current.connect();
            setWSProvider(ws_provider)

            await doctorAC.current.handler?.startMicRecording();
            await clientAC.current.handler?.startScreenRecording();

            await onStartStreaming();
        } catch (error: any) {
            if (error.message === 'MIC_BLOCKED' || error.name === 'NotAllowedError') {
                setMicDialogOpen(true);
            }

            await handleStopScreenShare();
            showMessage("Er is een fout opgetreden bij het starten van het scherm delen.", "error");
        }
    };

    const handleStopScreenShare = async () => {
        await doctorAC.current?.stop();
        await clientAC.current?.stop();
        await onStopStreaming();
    };

    const handleStartRecording = async () => {
        try {
            setIsRecording(true);

            doctorAC.current = new AudioChannel(
                language,
                onNewTranscript,
                () => {
                    handleStopRecording();
                    showMessage("Er is een fout opgetreden bij het starten van het scherm delen.", "error");
                }
            );
            const ws_provider = await doctorAC.current.connect();
            setWSProvider(ws_provider)

            await doctorAC.current.handler?.startMicRecording();
            await onStartStreaming();
        } catch (error: any) {
            if (error.message === 'MIC_BLOCKED' || error.name === 'NotAllowedError') {
                setMicDialogOpen(true);
            } else {
                showMessage("Er is een fout opgetreden bij het starten van het scherm delen.", "error");
            }

            await handleStopRecording();
        }
        try {
            if (isSupported && document.visibilityState === 'visible') await request();
        } catch (error: any) {
            console.error("Failed to request wake lock:", error);
        }
    };

    const handleStopRecording = async () => {
        await doctorAC.current?.stop();
        setIsRecording(false);
        await onStopStreaming();
        try {
            if (isSupported) await release();
        } catch (error) {
            console.error("Failed to release wake lock:", error);
        }
    };

    useEffect(() => {
        if (offlineTimeoutExceeded && isRecording){
            console.info("[Encounter] Offline for too long, stopping recording")
            handleStopScreenShare()
        }
    }, [offlineTimeoutExceeded, isRecording, handleStopScreenShare])

    if (templates.length === 0) return <CircularProgress sx={{display: "block", margin: "20px auto"}}/>;
    if (id && !encounter) return <CircularProgress sx={{display: "block", margin: "20px auto"}}/>;

    return (
        <Container maxWidth="xl">
            <Paper
                elevation={7}
                sx={{
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s',
                    '&:hover': {
                        borderColor: '#20A4A9',
                        boxShadow: '0 8px 16px rgba(25,118,210,0.15)'
                    },
                }}
            >
                <Stack>
                    <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between">
                        <Tabs
                            value={meetingType}
                            onChange={(_, newValue: TabValueType) => setMeetingType(newValue)}
                        >
                            <Tab
                                label="fysiek"
                                value="in-person"
                                disabled={isGeneratingReport || isRecording}
                            />
                            <Tab
                                label="online"
                                value="screen-sharing"
                                disabled={isGeneratingReport || isRecording}
                            />
                        </Tabs>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={isRecording ? <StopIcon/> : <FiberManualRecordIcon/>}
                            onClick={handleRecordingClick}
                            disabled={isGeneratingReport}
                            onMouseEnter={() => setRecordingHovered(true)}
                            onMouseLeave={() => setRecordingHovered(false)}
                            sx={{
                                minWidth: 160,
                                backgroundColor: isRecording ? "red" : "primary.main",
                                "&:hover": {
                                    backgroundColor: isRecording ? "darkred" : "primary.dark",
                                },
                            }}
                        >
                            {isRecording ? (isRecordingHovered ? 'Stop' : 'Recording') : 'Start'}
                        </Button>
                    </Stack>
                    <AIDialog
                        open={modifyAIOpen}
                        onClose={() => setModifyAIOpen(false)}
                        language={language}
                        onChange={setAIText}
                        onConfirm={() => {
                            handleModifyReportAI(aiText);
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Naam"
                        value={name}
                        onChange={onNameChange}
                        onBlur={onNameBlur}
                        onKeyDown={onNameKeyDown}
                        disabled={isRecording || isGeneratingReport}
                    />
                    <Stack direction={isMobile ? 'column' : 'row'}>
                        <FormControl>
                            <InputLabel id="template-label">Template</InputLabel>
                            <Select
                                labelId="template-label"
                                value={template?.id ?? ""}
                                label="Template"
                                onChange={onTemplateChange}
                                displayEmpty
                                variant="outlined"
                                disabled={isRecording || isGeneratingReport}
                            >
                                {templates
                                    .slice()
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((template) => (
                                        <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <InputLabel id="session-language-label">Gesprekstaal</InputLabel>
                            <Select
                                labelId="session-language-label"
                                value={language}
                                label="Gesprekstaal"
                                variant="outlined"
                                disabled={isRecording || isGeneratingReport}
                                onChange={(event) => {
                                    setLanguage(event.target.value)
                                }}
                            >
                                <MenuItem value="en">Engels</MenuItem>
                                <MenuItem value="nl">Nederlands</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>
                {encounter && (
                    <>
                        <Divider
                            sx={{margin: "32px 0"}}
                        />
                        <Stack
                            direction={isMobile && viewType === 'report' ? 'column' : 'row'}
                            justifyContent="space-between"
                            spacing={isMobile && viewType === 'report' ? 2 : 0}
                        >
                            <Tabs
                                value={viewType}
                                onChange={(_, newValue: ViewValueType) => setViewType(newValue)}
                                variant="scrollable"
                                scrollButtons="auto"
                            >
                                <Tab
                                    label="template"
                                    value="template"
                                />
                                <Tab
                                    label={'transcript' + (isBetaTester && wsProvider ? ` [${wsProvider}]` : '')}
                                    value="transcript"
                                />
                                <Tab
                                    label={'verslag' + (isBetaTester && aiProvider ? ` [${aiProvider}]` : '')}
                                    value="report"
                                />
                            </Tabs>
                            {viewType === 'report' && !isGeneratingReport && (
                                <Stack
                                    direction='row'
                                    spacing='12px'
                                    justifyContent="center"
                                    sx={{ml: 'auto'}}
                                >
                                    <IconButton
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: '8px',
                                            color: "secondary",
                                            backgroundColor: "secondary.main",
                                            border: "1px solid",
                                            borderColor: "primary.main",
                                            '&:hover': {
                                                animation: 'shake 0.5s ease-in-out ',
                                            },
                                            '@keyframes shake': {
                                                '0%': {transform: 'rotate(0deg)'},
                                                '25%': {transform: 'rotate(-5deg)'},
                                                '50%': {transform: 'rotate(5deg)'},
                                                '75%': {transform: 'rotate(-3deg)'},
                                                '100%': {transform: 'rotate(0deg)'},
                                            }
                                        }}
                                        onClick={() => {
                                            setAIText("");
                                            setModifyAIOpen(true);
                                        }}
                                        disabled={isGeneratingReport || isRecording}
                                    >
                                        <AutoFixHighIcon sx={{color: "primary.main"}}/>
                                    </IconButton>


                                    <IconButton
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: '8px',
                                            color: "secondary",
                                            backgroundColor: "secondary.main",
                                            border: "1px solid",
                                            borderColor: "primary.main",
                                        }}
                                        onClick={() => {
                                            onRegenerateReport()
                                        }}
                                        disabled={isGeneratingReport || isRecording}
                                    >
                                        <CachedIcon sx={{color: "primary.main"}}/>
                                    </IconButton>
                                    <IconButton
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: '8px',
                                            color: "primary.main",
                                            backgroundColor: "primary.main",
                                            border: "1px solid",
                                            borderColor: "primary.main",
                                            '&:hover': {
                                                backgroundColor: "primary.dark",
                                            },
                                        }}
                                        onClick={handleCopy}
                                        disabled={isGeneratingReport || isRecording}
                                    >
                                        {isIntegration ? (
                                            <SendIcon sx={{color: "secondary.main"}} fontSize="small"/>
                                        ) : (
                                            isGeneratingReport || isRecording ?
                                                (<ContentCopyIcon sx={{color: "primary.main"}} fontSize="small"/>) :
                                                (<ContentCopyIcon sx={{color: "secondary.main"}} fontSize="small"/>)
                                        )}
                                    </IconButton>
                                </Stack>
                            )}

                        </Stack>
                        {viewType === 'template' && template && (
                            <TemplateDetails template={template}/>
                        )}
                        {viewType === 'transcript' && template && (
                            <TranscriptBox transcript={encounter?.transcript ?? ""}
                                           uncompletedTranscript={uncompletedTranscript}/>
                        )}
                        {viewType === 'report' && template && (
                            <>
                                {isGeneratingReport && (<CircularProgress sx={{display: "block", margin: "auto"}}/>)}
                                <ReportBox
                                    encounterId={encounter.id}
                                    report={encounter?.report ?? {}}
                                    language={language}
                                    onAddScore={onAddScore}
                                    disabled={isGeneratingReport || isRecording}
                                    onReportChange={handleReportChange}
                                    onModifyAI={handleModifyItemAI}
                                />
                            </>
                        )}
                    </>
                )}
            </Paper>
            <MicPermissionDialog
                open={micDialogOpen}
                onClose={() => setMicDialogOpen(false)}
            />

            <div ref={bottomRef}/>
            <Slide direction="down" in={showStickyPanel} mountOnEnter unmountOnExit>
                <Paper
                    sx={{
                        position: 'fixed',
                        top: 64,
                        left: sidebarOpen ? drawerWidth : 0,
                        transform: 'translateX(-50%)',
                        width: `calc(100% - ${sidebarOpen ? drawerWidth : 0}px)`,
                        height: '90px',
                        zIndex: 1200,
                        p: 0,
                        borderRadius: '1px',
                        bgcolor: "background.default",
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" p='24px'>
                        <Tabs
                            value={viewType}
                            onChange={(_, newValue: ViewValueType) => setViewType(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab
                                label="template"
                                value="template"
                            />
                            <Tab
                                label="transcript"
                                value="transcript"
                            />
                            <Tab
                                label="verslag"
                                value="report"
                            />
                        </Tabs>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={isRecording ? <StopIcon/> : <FiberManualRecordIcon/>}
                            onClick={handleRecordingClick}
                            disabled={isGeneratingReport}
                            onMouseEnter={() => setRecordingHovered(true)}
                            onMouseLeave={() => setRecordingHovered(false)}
                            sx={{
                                backgroundColor: isRecording ? "red" : "primary.main",
                                "&:hover": {
                                    backgroundColor: isRecording ? "darkred" : "primary.dark",
                                },
                            }}
                        >
                            {isRecording ? (isRecordingHovered ? 'Stop' : 'Recording') : 'Start'}
                        </Button>
                    </Stack>
                </Paper>
            </Slide>
        </Container>
    );
};

export default Encounter;
